import { CheckIcon } from "@heroicons/react/24/solid";
import { TOptions } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/atoms-react/button/Button.react";
import { cn } from "@/features/utils/cn";
import { Themes } from "@/types/theme";
import { CompanyOnboardingStep } from "../company-onboarding";

type CompanyOnboarding_StepperProps = {
  step: CompanyOnboardingStep;
  previous: () => void;
  next: () => void;
  nextLoading: boolean;
  enableAnimation?: boolean;
};
export const CompanyOnboarding_Stepper = ({
  step,
  previous,
  next,
  nextLoading,
  enableAnimation = true
}: CompanyOnboarding_StepperProps) => {
  const { t } = useTranslation(["pass"]);
  const tr = (key: string, options?: TOptions) =>
    t(`company_onboarding.stepper_content.${key}`, options);
  const stepList = Object.values(CompanyOnboardingStep);
  const stepIndex = useMemo(() => stepList.indexOf(step), [step]);

  return (
    <div className="mb-3 w-full rounded-sm border border-primary bg-primary/20 px-5 py-3 dark:bg-primary-700">
      <div className="mt-5 mb-20 flex flex-row flex-nowrap items-center justify-between gap-3 px-3 xs:px-24 font-medium">
        {stepList.map((s, i) => [
          <div
            key={`CompanyOnboarding_Stepper_${s}`}
            className="flex flex-col items-center justify-center"
          >
            <div
              className={cn(
                "line flex size-8 items-center justify-center rounded rounded-full font-lg",
                enableAnimation && "transition-all duration-200",
                stepIndex == i
                  ? "bg-primary text-white"
                  : i < stepIndex
                    ? "bg-[#01B57C] text-white"
                    : "bg-white text-black"
              )}
            >
              {i < stepIndex ? <CheckIcon className="size-6 text-white" /> : <div>{i + 1}</div>}
            </div>
            <div className="relative w-full">
              <div className="tranform absolute top-3 left-1/2 line-clamp-3 w-24 xs:w-36 -translate-x-1/2 text-center text-black text-xs xs:text-base sm:line-clamp-2 dark:text-white">
                {tr(`step${i + 1}`)}
              </div>
            </div>
          </div>,
          ...(i < stepList.length - 1
            ? [
                <div
                  key={`CompanyOnboarding_Stepper_Separator_${i}`}
                  className="relative h-1 w-full bg-white"
                >
                  <div
                    className={cn(
                      "h-full bg-[#01B57C]",
                      i < stepIndex ? "w-full" : "w-0",
                      enableAnimation && "transition-width duration-200"
                    )}
                  ></div>
                </div>
              ]
            : [])
        ])}
      </div>

      <div className="flex w-full items-center justify-between gap-5">
        <div>
          {stepIndex > 0 && stepIndex < stepList.length - 1 && (
            <Button className="w-auto" onClick={() => previous()} theme={Themes.white}>
              {tr("previous")}
            </Button>
          )}
        </div>

        {stepIndex < stepList.length - 1 && (
          <Button
            loading={nextLoading}
            className="w-auto"
            onClick={() => next()}
            theme={Themes.white}
          >
            {stepIndex < stepList.length - 2 ? tr("next") : tr("finalize")}
          </Button>
        )}
      </div>
    </div>
  );
};
